import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, BarChart3, Loader2, Music, MousePointerClick, Play, TrendingUp } from "lucide-react";
import { api } from "@/App";
import MuslinkLogo from "@/components/MuslinkLogo";
import { useLanguage } from "@/contexts/LanguageContext";

const CHART_COPY = {
  en: {
    title: "Chart",
    subtitle: "Popular user releases on Mus.Link",
    empty: "No releases in the chart yet",
    views: "views",
    clicks: "clk.",
    ctr: "CTR",
    open: "Open release",
  },
  ru: {
    title: "Чарт",
    subtitle: "Популярные релизы пользователей Mus.Link",
    empty: "В чарте пока нет релизов",
    views: "просм.",
    clicks: "кл.",
    ctr: "CTR",
    open: "Открыть релиз",
  },
  es: {
    title: "Chart",
    subtitle: "Lanzamientos populares de usuarios en Mus.Link",
    empty: "Aún no hay lanzamientos en el chart",
    views: "vist.",
    clicks: "clics",
    ctr: "CTR",
    open: "Abrir lanzamiento",
  },
};

function getCopy(language) {
  return CHART_COPY[language] || CHART_COPY.en;
}

function getImageUrl(url) {
  if (!url) return null;
  return url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
}

export default function Chart() {
  const { language } = useLanguage();
  const copy = getCopy(language);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChart = async () => {
      try {
        const response = await api.get("/chart?limit=50");
        setItems(response.data.items || []);
      } catch (error) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchChart();
  }, []);

  return (
    <div className="min-h-screen bg-[#070809] text-white">
      <div className="fixed inset-0 bg-[linear-gradient(180deg,rgba(217,70,239,0.15),rgba(7,8,9,0.9)_34%,#070809_100%)]" />
      <main className="relative z-10 mx-auto w-full max-w-[1500px] px-4 py-7 sm:px-6 lg:px-10">
        <header className="mb-8 border-b border-white/10 pb-7 sm:mb-9">
          <div className="mb-10 flex items-center justify-between gap-4">
            <Link to="/" className="inline-flex">
              <MuslinkLogo height={28} theme="dark" />
            </Link>
            <div className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/8 px-4 py-3 text-sm text-zinc-300 backdrop-blur-xl">
              <BarChart3 className="h-4 w-4 text-white" />
              {items.length}
            </div>
          </div>
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/8 px-3 py-1.5 text-xs text-zinc-300 backdrop-blur-md">
              <TrendingUp className="h-3.5 w-3.5 text-white" />
              Mus.Link
            </div>
            <h1 className="text-4xl font-semibold leading-tight sm:text-6xl">{copy.title}</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300 sm:text-base">{copy.subtitle}</p>
          </div>
        </header>

        {loading ? (
          <div className="flex min-h-[360px] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-white" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/6 px-6 py-16 text-center backdrop-blur-xl">
            <Music className="mx-auto mb-4 h-14 w-14 text-zinc-600" />
            <p className="text-zinc-400">{copy.empty}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((item, index) => {
              const cover = getImageUrl(item.cover_image);
              return (
                <motion.article
                  key={item.id}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.035, duration: 0.35 }}
                  className="group overflow-hidden rounded-2xl border border-white/10 bg-white/8 p-3 backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-white/60 hover:bg-white/12"
                >
                  <Link to={`/${item.slug}`} className="block">
                    <div className="relative aspect-square overflow-hidden rounded-xl bg-zinc-900">
                      {cover ? (
                        <img src={cover} alt={item.release_title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <Music className="h-12 w-12 text-zinc-600" />
                        </div>
                      )}
                      <div className="absolute left-3 top-3 rounded-full bg-white px-3 py-1 text-sm font-semibold text-zinc-950">#{item.rank}</div>
                    </div>
                    <div className="px-1 pb-1 pt-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h2 className="truncate text-base font-semibold text-white">{item.release_title}</h2>
                          <p className="mt-1 truncate text-sm text-zinc-400">{item.artist_name}</p>
                        </div>
                        <ArrowUpRight className="mt-0.5 h-5 w-5 shrink-0 text-white opacity-50 transition-opacity group-hover:opacity-100" />
                      </div>
                      <div className="mt-4 grid grid-cols-3 gap-2 text-[11px] text-zinc-400">
                        <span className="rounded-xl bg-white/6 px-2 py-2"><Play className="mb-1 h-3.5 w-3.5 text-white" />{item.views} {copy.views}</span>
                        <span className="rounded-xl bg-white/6 px-2 py-2"><MousePointerClick className="mb-1 h-3.5 w-3.5 text-white" />{item.clicks} {copy.clicks}</span>
                        <span className="rounded-xl bg-white/6 px-2 py-2"><BarChart3 className="mb-1 h-3.5 w-3.5 text-white" />{item.ctr}% {copy.ctr}</span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
